import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Package, Tag, Grid, Settings, LogOut, Plus, Edit, Trash2, Upload, Warehouse, FileText, Users, DollarSign, Menu, X, Check, Square, CheckSquare, ArrowRight, ArchiveX, BarChart3, UserCheck, ListChecks } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import InventoryPanel from './InventoryPanel';
import BillingPanel from './BillingPanel';
import AnalyticsPanel from './AnalyticsPanel';
import AccountPanel from './AccountPanel';
import ClientsPanel from './ClientsPanel';
import PreOrdersPanel from './PreOrdersPanel';
import ImageUploader from '../../components/ImageUploader';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('products');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showProductDialog, setShowProductDialog] = useState(false);
  
  // Estado para edición rápida de stock
  const [quickEditStock, setQuickEditStock] = useState({ productId: null, value: 0 });
  const [savingStock, setSavingStock] = useState(false);
  
  // Filter state for products
  const [productCategoryFilter, setProductCategoryFilter] = useState('all');
  const [productBrandFilter, setProductBrandFilter] = useState('all');
  
  // Selection state for bulk actions
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [showBulkTransferDialog, setShowBulkTransferDialog] = useState(false);
  const [bulkTransferTarget, setBulkTransferTarget] = useState('stock');

  useEffect(() => {
    if (!user) navigate('/admin/login');
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [inStockRes, categoriesRes, brandsRes, configRes] = await Promise.all([
        api.get('/admin/products/in-stock', { headers: { Authorization: `Bearer ${token}` } }),
        api.get('/categories'),
        api.get('/brands'),
        api.get('/config')
      ]);
      setProducts(inStockRes.data);
      setCategories(categoriesRes.data);
      setBrands(brandsRes.data);
      setConfig(configRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('¿Deseas archivar este producto?\n\nEl producto será movido a la bodega "Archivados". Podrás eliminarlo definitivamente desde el panel de Inventario > Archivados.')) return;
    try {
      const token = localStorage.getItem('token');
      await api.post(`/products/${id}/archive`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Producto archivado correctamente');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error archivando producto');
    }
  };

  const handleSaveProduct = async (productData) => {
    try {
      if (editingProduct?.id) {
        await api.put(`/products/${editingProduct.id}`, productData);
        toast.success('Producto actualizado');
      } else {
        await api.post('/products', productData);
        toast.success('Producto creado');
      }
      setShowProductDialog(false);
      setEditingProduct(null);
      fetchData();
    } catch (error) {
      toast.error('Error guardando producto');
    }
  };

  // Función para guardar stock rápidamente
  const handleQuickStockSave = async (productId) => {
    if (savingStock) return;
    setSavingStock(true);
    try {
      await api.put(`/products/${productId}`, { stock: quickEditStock.value });
      toast.success('Stock actualizado');
      setQuickEditStock({ productId: null, value: 0 });
      fetchData();
    } catch (error) {
      toast.error('Error actualizando stock');
    } finally {
      setSavingStock(false);
    }
  };

  // Cancelar edición rápida de stock
  const cancelQuickEdit = () => {
    setQuickEditStock({ productId: null, value: 0 });
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSidebarOpen(false); // Cerrar sidebar en móvil al cambiar de tab
  };

  // Filtrar productos por categoría y marca
  const filteredProducts = products.filter(p => {
    const categoryMatch = productCategoryFilter === 'all' || p.category === productCategoryFilter;
    const brandMatch = productBrandFilter === 'all' || p.brand === productBrandFilter;
    return categoryMatch && brandMatch;
  });

  // Selection handlers for products
  const toggleProductSelection = (productId) => {
    const newSelection = new Set(selectedProducts);
    if (newSelection.has(productId)) {
      newSelection.delete(productId);
    } else {
      newSelection.add(productId);
    }
    setSelectedProducts(newSelection);
  };

  const selectAllProducts = () => {
    const allIds = new Set(products.map(p => p.id));
    setSelectedProducts(allIds);
  };

  const clearProductSelection = () => {
    setSelectedProducts(new Set());
  };

  // Bulk delete products
  const handleBulkDeleteProducts = async () => {
    if (selectedProducts.size === 0) return;
    
    if (!window.confirm(`¿Está seguro de eliminar ${selectedProducts.size} producto(s)? Esta acción no se puede deshacer.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      let deleted = 0;
      
      for (const id of selectedProducts) {
        try {
          await api.delete(`/products/${id}`, { headers: { Authorization: `Bearer ${token}` } });
          deleted++;
        } catch (err) {
          console.error(`Error deleting product ${id}:`, err);
        }
      }
      
      toast.success(`${deleted} producto(s) eliminado(s)`);
      setSelectedProducts(new Set());
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar productos');
    }
  };

  // Bulk transfer products
  const handleBulkTransfer = async () => {
    if (selectedProducts.size === 0) return;
    
    try {
      const token = localStorage.getItem('token');
      let transferred = 0;
      
      for (const productId of selectedProducts) {
        const product = products.find(p => p.id === productId);
        if (!product) continue;
        
        const inventory = product.inventory || { china: 0, contenedor: 0, stock: 0 };
        const sourceWarehouse = bulkTransferTarget === 'stock' ? 'contenedor' : (bulkTransferTarget === 'contenedor' ? 'china' : 'contenedor');
        const quantity = inventory[sourceWarehouse] || 0;
        
        if (quantity > 0) {
          try {
            await api.post('/inventory/transfer', {
              product_id: productId,
              from_warehouse: sourceWarehouse,
              to_warehouse: bulkTransferTarget,
              quantity: quantity
            }, { headers: { Authorization: `Bearer ${token}` } });
            transferred++;
          } catch (err) {
            console.error(`Error transferring product ${productId}:`, err);
          }
        }
      }
      
      toast.success(`${transferred} producto(s) transferido(s) a ${bulkTransferTarget.toUpperCase()}`);
      setSelectedProducts(new Set());
      setShowBulkTransferDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Error en la transferencia masiva');
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A] border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)} 
            className="p-2 hover:bg-white/10 rounded transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div>
            <h1 className="font-['Playfair_Display'] text-lg">HAUTE</h1>
            <p className="text-[10px] text-white/60 uppercase tracking-wider">Admin</p>
          </div>
        </div>
        <button 
          onClick={handleLogout} 
          className="p-2 hover:bg-white/10 rounded transition-colors text-white/60"
        >
          <LogOut className="w-5 h-5" />
        </button>
      </div>

      <div className="flex">
        {/* Overlay para móvil */}
        {sidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black/60 z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside className={`
          w-64 bg-[#0A0A0A] border-r border-white/10 min-h-screen fixed z-50
          transition-transform duration-300 ease-in-out
          lg:translate-x-0 lg:static
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <div className="p-6 hidden lg:block">
            <h1 className="font-['Playfair_Display'] text-2xl mb-2">HAUTE</h1>
            <p className="text-xs text-white/60 uppercase tracking-[0.2em]">Admin Panel</p>
          </div>
          
          {/* Espaciado para header móvil */}
          <div className="h-16 lg:hidden" />
          
          <nav className="px-3">
            <button onClick={() => handleTabChange('products')} data-testid="tab-products" className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm mb-1 transition-colors duration-300 ${activeTab === 'products' ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white'}`}>
              <Package className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">Productos</span>
              <span className="ml-auto text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">{products.length}</span>
            </button>
            <button onClick={() => handleTabChange('inventory')} data-testid="tab-inventory" className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm mb-1 transition-colors duration-300 ${activeTab === 'inventory' ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white'}`}>
              <Warehouse className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">Inventario</span>
            </button>
            <button onClick={() => handleTabChange('billing')} data-testid="tab-billing" className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm mb-1 transition-colors duration-300 ${activeTab === 'billing' ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white'}`}>
              <FileText className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">Facturación</span>
            </button>
            <button onClick={() => handleTabChange('clients')} data-testid="tab-clients" className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm mb-1 transition-colors duration-300 ${activeTab === 'clients' ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white'}`}>
              <UserCheck className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">Clientes</span>
            </button>
            <button onClick={() => handleTabChange('preorders')} data-testid="tab-preorders" className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm mb-1 transition-colors duration-300 ${activeTab === 'preorders' ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white'}`}>
              <ListChecks className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">Pre-pedidos</span>
            </button>
            <button onClick={() => handleTabChange('analytics')} data-testid="tab-analytics" className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm mb-1 transition-colors duration-300 ${activeTab === 'analytics' ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white'}`}>
              <BarChart3 className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">Estadísticas</span>
            </button>
            <button onClick={() => handleTabChange('categories')} data-testid="tab-categories" className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm mb-1 transition-colors duration-300 ${activeTab === 'categories' ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white'}`}>
              <Grid className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">Categorías</span>
            </button>
            <button onClick={() => handleTabChange('brands')} data-testid="tab-brands" className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm mb-1 transition-colors duration-300 ${activeTab === 'brands' ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white'}`}>
              <Tag className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">Marcas</span>
            </button>
            <button onClick={() => handleTabChange('config')} data-testid="tab-config" className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm mb-1 transition-colors duration-300 ${activeTab === 'config' ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white'}`}>
              <Settings className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">Configuración</span>
            </button>
            <button onClick={() => handleTabChange('account')} data-testid="tab-account" className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm mb-1 transition-colors duration-300 ${activeTab === 'account' ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white'}`}>
              <Users className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">Mi Cuenta</span>
            </button>
          </nav>
          
          <div className="absolute bottom-0 w-64 p-3 border-t border-white/10 hidden lg:block">
            <button onClick={handleLogout} data-testid="logout-button" className="w-full flex items-center gap-3 px-4 py-3 rounded-sm text-white/60 hover:text-white transition-colors duration-300">
              <LogOut className="w-4 h-4" />
              <span className="text-sm">Cerrar Sesión</span>
            </button>
          </div>
        </aside>
        
        {/* Main Content - ajustado para móvil */}
        <main className="flex-1 lg:ml-64 p-4 md:p-8 lg:p-12 pt-20 lg:pt-12 min-h-screen">
          {activeTab === 'products' && (
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 md:mb-8 gap-4">
                <div>
                  <h2 className="text-xl md:text-2xl font-semibold mb-1 md:mb-2">Productos Activos</h2>
                  <p className="text-xs md:text-sm text-white/60">{products.length} productos con stock</p>
                </div>
                <button onClick={() => { setEditingProduct(null); setShowProductDialog(true); }} data-testid="add-product-button" className="bg-white text-black hover:bg-gray-200 transition-colors duration-300 px-4 md:px-6 py-2.5 md:py-3 rounded-sm text-xs uppercase tracking-wider font-semibold flex items-center justify-center gap-2 w-full sm:w-auto">
                  <Plus className="w-4 h-4" />
                  Nuevo Producto
                </button>
              </div>
              
              {/* Selection Actions Bar */}
              {selectedProducts.size > 0 && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <CheckSquare className="w-4 h-4 text-blue-400" />
                    <span className="text-sm text-blue-400">{selectedProducts.size} producto(s) seleccionado(s)</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={clearProductSelection}
                      className="px-3 py-1.5 text-xs text-white/60 hover:text-white border border-white/10 rounded"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={() => setShowBulkTransferDialog(true)}
                      className="px-3 py-1.5 text-xs bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 rounded flex items-center gap-1"
                    >
                      <ArrowRight className="w-3 h-3" />
                      Transferir
                    </button>
                    <button
                      onClick={handleBulkDeleteProducts}
                      className="px-3 py-1.5 text-xs bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" />
                      Eliminar
                    </button>
                  </div>
                </div>
              )}
              
              {/* Quick Selection Buttons */}
              <div className="flex flex-wrap gap-2 mb-4">
                <button
                  onClick={selectAllProducts}
                  className="px-3 py-1.5 text-xs text-white/60 hover:text-white border border-white/10 rounded flex items-center gap-1"
                >
                  <CheckSquare className="w-3 h-3" />
                  Seleccionar Todo
                </button>
                {selectedProducts.size > 0 && (
                  <button
                    onClick={clearProductSelection}
                    className="px-3 py-1.5 text-xs text-white/40 hover:text-white border border-white/10 rounded"
                  >
                    Limpiar ({selectedProducts.size})
                  </button>
                )}
              </div>
              
              {/* Filtros de productos */}
              <div className="flex flex-wrap gap-3 mb-4 p-3 bg-[#050505] border border-white/5 rounded">
                <div className="flex items-center gap-2 flex-1 min-w-[150px]">
                  <label className="text-xs text-white/40">Categoría:</label>
                  <select
                    value={productCategoryFilter}
                    onChange={(e) => setProductCategoryFilter(e.target.value)}
                    className="flex-1 bg-[#0A0A0A] border border-white/10 px-3 py-1.5 text-xs outline-none cursor-pointer rounded"
                  >
                    <option value="all">Todas ({products.length})</option>
                    {categories.map(cat => {
                      const count = products.filter(p => p.category === cat.slug).length;
                      return count > 0 ? (
                        <option key={cat.id} value={cat.slug}>{cat.name} ({count})</option>
                      ) : null;
                    })}
                  </select>
                </div>
                <div className="flex items-center gap-2 flex-1 min-w-[150px]">
                  <label className="text-xs text-white/40">Marca:</label>
                  <select
                    value={productBrandFilter}
                    onChange={(e) => setProductBrandFilter(e.target.value)}
                    className="flex-1 bg-[#0A0A0A] border border-white/10 px-3 py-1.5 text-xs outline-none cursor-pointer rounded"
                  >
                    <option value="all">Todas ({products.length})</option>
                    {brands.map(brand => {
                      const count = products.filter(p => p.brand === brand.name).length;
                      return count > 0 ? (
                        <option key={brand.id} value={brand.name}>{brand.name} ({count})</option>
                      ) : null;
                    })}
                  </select>
                </div>
                {(productCategoryFilter !== 'all' || productBrandFilter !== 'all') && (
                  <button
                    onClick={() => { setProductCategoryFilter('all'); setProductBrandFilter('all'); }}
                    className="px-3 py-1.5 text-xs text-white/40 hover:text-white border border-white/10 rounded"
                  >
                    Limpiar filtros
                  </button>
                )}
              </div>
              
              {/* Contador de productos filtrados */}
              {(productCategoryFilter !== 'all' || productBrandFilter !== 'all') && (
                <p className="text-xs text-white/40 mb-3">
                  Mostrando {filteredProducts.length} de {products.length} productos
                </p>
              )}
              
              <div className="grid grid-cols-1 gap-3 md:gap-4">
                {filteredProducts.map((product) => (
                  <div key={product.id} data-testid={`product-row-${product.id}`} className={`bg-[#0A0A0A] border p-3 md:p-4 flex flex-col sm:flex-row sm:items-center justify-between hover:border-white/20 transition-all duration-300 gap-3 ${selectedProducts.has(product.id) ? 'border-blue-500/50 bg-blue-500/5' : 'border-white/10'}`}>
                    {/* Checkbox */}
                    <button 
                      onClick={() => toggleProductSelection(product.id)}
                      className="flex-shrink-0 self-start sm:self-center"
                    >
                      {selectedProducts.has(product.id) ? (
                        <CheckSquare className="w-5 h-5 text-blue-400" />
                      ) : (
                        <Square className="w-5 h-5 text-white/30 hover:text-white/60" />
                      )}
                    </button>
                    
                    <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
                      {product.images[0] && (
                        <img src={product.images[0]} alt={product.name} className="w-12 h-12 md:w-16 md:h-16 object-cover flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-xs md:text-sm mb-1 truncate">{product.name}</h3>
                        <p className="text-[10px] md:text-xs text-white/60 truncate">{product.brand} · SKU: {product.sku}</p>
                        <p className="text-[10px] md:text-xs text-white/80 mt-1">${product.price.toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end gap-3 md:gap-4">
                      <div className="text-left sm:text-right">
                        <p className="text-[10px] text-white/40 uppercase tracking-wider">Stock</p>
                        {quickEditStock.productId === product.id ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="0"
                              value={quickEditStock.value}
                              onChange={(e) => setQuickEditStock({ ...quickEditStock, value: parseInt(e.target.value) || 0 })}
                              className="w-16 bg-[#1a1a1a] border border-green-500/50 px-2 py-1 text-sm text-green-400 text-center outline-none focus:border-green-400"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleQuickStockSave(product.id);
                                if (e.key === 'Escape') cancelQuickEdit();
                              }}
                            />
                            <button
                              onClick={() => handleQuickStockSave(product.id)}
                              disabled={savingStock}
                              className="p-1 bg-green-500/20 border border-green-500/50 hover:bg-green-500/30 rounded-sm transition-all"
                              data-testid={`confirm-stock-${product.id}`}
                            >
                              <Check className="w-4 h-4 text-green-400" />
                            </button>
                            <button
                              onClick={cancelQuickEdit}
                              className="p-1 bg-red-500/20 border border-red-500/50 hover:bg-red-500/30 rounded-sm transition-all"
                            >
                              <X className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setQuickEditStock({ productId: product.id, value: product.stock })}
                            className="text-sm font-semibold text-green-400 hover:text-green-300 hover:underline cursor-pointer transition-all"
                            data-testid={`quick-edit-stock-${product.id}`}
                            title="Clic para editar stock"
                          >
                            {product.stock}
                          </button>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => { setEditingProduct(product); setShowProductDialog(true); }} data-testid={`edit-product-${product.id}`} className="p-2 border border-white/10 hover:border-white/40 rounded-sm transition-all duration-300">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDeleteProduct(product.id)} data-testid={`archive-product-${product.id}`} className="p-2 border border-white/10 hover:border-orange-500/40 hover:text-orange-500 rounded-sm transition-all duration-300" title="Archivar producto">
                          <ArchiveX className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Bulk Transfer Dialog */}
              {showBulkTransferDialog && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowBulkTransferDialog(false)}>
                  <div className="bg-[#0A0A0A] border border-white/10 p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
                    <h3 className="text-lg font-semibold mb-4">Transferir {selectedProducts.size} Producto(s)</h3>
                    <p className="text-sm text-white/60 mb-4">Seleccione la bodega destino para transferir todos los productos seleccionados.</p>
                    
                    <div className="space-y-2 mb-6">
                      {['china', 'contenedor', 'stock'].map(warehouse => (
                        <button
                          key={warehouse}
                          onClick={() => setBulkTransferTarget(warehouse)}
                          className={`w-full px-4 py-3 text-left border rounded transition-all ${
                            bulkTransferTarget === warehouse 
                              ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' 
                              : 'border-white/10 hover:border-white/30'
                          }`}
                        >
                          <span className="font-semibold uppercase">{warehouse}</span>
                          <p className="text-xs text-white/40 mt-1">
                            {warehouse === 'china' && 'Bodega en China'}
                            {warehouse === 'contenedor' && 'En tránsito'}
                            {warehouse === 'stock' && 'Disponible para venta'}
                          </p>
                        </button>
                      ))}
                    </div>
                    
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowBulkTransferDialog(false)}
                        className="flex-1 px-4 py-2 border border-white/10 text-sm hover:bg-white/5"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleBulkTransfer}
                        className="flex-1 px-4 py-2 bg-emerald-600 text-white text-sm hover:bg-emerald-500"
                      >
                        Transferir
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          
          {activeTab === 'inventory' && (
            <div>
              <h2 className="text-xl md:text-2xl font-semibold mb-6 md:mb-8">Inventario</h2>
              <InventoryPanel onInventoryChange={fetchData} />
            </div>
          )}
          
          {activeTab === 'billing' && (
            <div>
              <h2 className="text-xl md:text-2xl font-semibold mb-6 md:mb-8">Facturación</h2>
              <BillingPanel />
            </div>
          )}

          {activeTab === 'clients' && (
            <div>
              <ClientsPanel />
            </div>
          )}

          {activeTab === 'preorders' && (
            <div>
              <PreOrdersPanel />
            </div>
          )}
          
          {activeTab === 'analytics' && (
            <div>
              <AnalyticsPanel />
            </div>
          )}
          
          {activeTab === 'account' && (
            <div>
              <AccountPanel />
            </div>
          )}
          
          {activeTab === 'categories' && (
            <CategoriesPanel categories={categories} onUpdate={fetchData} />
          )}
          
          {activeTab === 'brands' && (
            <BrandsPanel brands={brands} onUpdate={fetchData} />
          )}
          
          {activeTab === 'config' && config && (
            <ConfigPanel config={config} onUpdate={fetchData} />
          )}
        </main>
      </div>
      
      {showProductDialog && (
        <ProductDialog
          product={editingProduct}
          categories={categories}
          brands={brands}
          onSave={handleSaveProduct}
          onClose={() => { setShowProductDialog(false); setEditingProduct(null); }}
        />
      )}
    </div>
  );
};

// Tallas predeterminadas (el usuario puede agregar más escribiendo)
const DEFAULT_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL', '7XL', '8XL'];

const ProductDialog = ({ product, categories, brands, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: '', brand: '', sku: '', price: 0, cost_price: null, promotional_price: null,
    short_description: '', full_description: '', images: [], main_image_index: 0, sizes: [],
    colors: [], stock: 0, cantidad: '', status: 'in_stock', category: '', featured: false, featured_home: false
  });
  const [customSizeInput, setCustomSizeInput] = useState('');
  
  // Actualizar formData cuando cambia el producto
  useEffect(() => {
    if (product) {
      setFormData({
        ...product,
        stock: product.stock || product.inventory?.stock || 0,
        cost_price: product.cost_price || null,
        promotional_price: product.promotional_price || null,
        images: product.images || [],
        main_image_index: product.main_image_index || 0,
        sizes: product.sizes || [],
        colors: product.colors || [],
      });
    } else {
      setFormData({
        name: '', brand: '', sku: '', price: 0, cost_price: null, promotional_price: null,
        short_description: '', full_description: '', images: [], main_image_index: 0, sizes: [],
        colors: [], stock: 0, cantidad: '', status: 'in_stock', category: '', featured: false, featured_home: false
      });
    }
  }, [product]);
  
  // Combinar tallas predeterminadas con tallas personalizadas existentes en productos
  const allAvailableSizes = [...new Set([
    ...DEFAULT_SIZES,
    ...(formData.sizes || []).filter(s => !DEFAULT_SIZES.includes(s))
  ])];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const toggleSize = (size) => {
    const currentSizes = formData.sizes || [];
    if (currentSizes.includes(size)) {
      setFormData({ ...formData, sizes: currentSizes.filter(s => s !== size) });
    } else {
      setFormData({ ...formData, sizes: [...currentSizes, size] });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-white/10 p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-xl font-semibold mb-6">{product ? 'Editar Producto' : 'Nuevo Producto'}</h2>
        
        <form onSubmit={handleSubmit} data-testid="product-form">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] mb-2 text-white/80">Nombre</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] mb-2 text-white/80">SKU</label>
              <input type="text" value={formData.sku} onChange={(e) => setFormData({...formData, sku: e.target.value})} required className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none" />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] mb-2 text-white/80">Marca</label>
              <select 
                value={formData.brand} 
                onChange={(e) => setFormData({...formData, brand: e.target.value})} 
                required 
                className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none"
              >
                <option value="">Seleccionar marca...</option>
                {brands.map((brand) => (
                  <option key={brand.id} value={brand.name}>{brand.name}</option>
                ))}
              </select>
              {brands.length === 0 && (
                <p className="text-xs text-yellow-400/80 mt-1">No hay marcas. Créalas en la sección Marcas.</p>
              )}
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] mb-2 text-white/80">Categoría</label>
              <select 
                value={formData.category} 
                onChange={(e) => setFormData({...formData, category: e.target.value})} 
                required 
                className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none"
              >
                <option value="">Seleccionar categoría...</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.slug}>{cat.name}</option>
                ))}
              </select>
              {categories.length === 0 && (
                <p className="text-xs text-yellow-400/80 mt-1">No hay categorías. Créalas en la sección Categorías.</p>
              )}
            </div>
          </div>
          
          {/* Precios */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] mb-2 text-white/80">Precio Venta</label>
              <input type="number" value={formData.price} onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value) || 0})} required className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] mb-2 text-white/80">
                Precio Costo
                <span className="text-yellow-400/60 normal-case ml-2">(interno)</span>
              </label>
              <input 
                type="number" 
                value={formData.cost_price || ''} 
                onChange={(e) => setFormData({...formData, cost_price: e.target.value ? parseFloat(e.target.value) : null})} 
                placeholder="0"
                className="w-full bg-[#121212] border border-yellow-500/20 focus:border-yellow-500/40 px-4 py-3 text-white text-sm outline-none" 
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] mb-2 text-white/80">
                Stock
                <span className="text-white/40 normal-case ml-2">(interno)</span>
              </label>
              <input 
                type="number" 
                min="0"
                value={formData.stock || 0} 
                onChange={(e) => setFormData({...formData, stock: parseInt(e.target.value) || 0})} 
                required 
                className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none" 
              />
            </div>
          </div>
          <p className="text-xs text-white/40 mb-4">El precio de costo y stock son solo para control interno. No se muestran al público.</p>
          
          <div className="mb-4">
            <label className="block text-xs uppercase tracking-[0.2em] mb-2 text-white/80">
              Cantidad
              <span className="text-white/40 normal-case ml-2">(visible al cliente)</span>
            </label>
            <input type="text" value={formData.cantidad || ''} onChange={(e) => setFormData({...formData, cantidad: e.target.value})} placeholder="Ej: paquete de 10 surtido tallas" className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none" />
          </div>
          
          {/* Selector de Tallas - Dinámico */}
          <div className="mb-4">
            <label className="block text-xs uppercase tracking-[0.2em] mb-3 text-white/80">
              Tallas Disponibles
              <span className="text-white/40 normal-case ml-2">(selecciona o agrega nuevas)</span>
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {allAvailableSizes.map((size) => {
                const isSelected = (formData.sizes || []).includes(size);
                return (
                  <button
                    key={size}
                    type="button"
                    onClick={() => toggleSize(size)}
                    className={`px-4 py-2 text-xs font-semibold border transition-all duration-200 ${
                      isSelected 
                        ? 'bg-white text-black border-white' 
                        : 'bg-transparent text-white/60 border-white/20 hover:border-white/40 hover:text-white'
                    }`}
                  >
                    {size}
                  </button>
                );
              })}
            </div>
            {/* Agregar talla personalizada */}
            <div className="flex gap-2">
              <input 
                type="text" 
                value={customSizeInput}
                onChange={(e) => setCustomSizeInput(e.target.value.toUpperCase())}
                placeholder="Nueva talla (ej: ÚNICO, 32, 40)"
                className="flex-1 bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-2 text-white text-sm outline-none"
              />
              <button 
                type="button" 
                onClick={() => {
                  if (customSizeInput && !formData.sizes?.includes(customSizeInput)) {
                    setFormData({...formData, sizes: [...(formData.sizes || []), customSizeInput]});
                    setCustomSizeInput('');
                  }
                }}
                className="px-4 py-2 bg-white/10 border border-white/20 hover:bg-white/20 transition-colors text-xs"
              >
                Agregar
              </button>
            </div>
            {(formData.sizes || []).length > 0 && (
              <p className="text-xs text-green-400/80 mt-3">
                Tallas seleccionadas: {formData.sizes.join(', ')}
              </p>
            )}
          </div>
          
          <div className="mb-4">
            <label className="block text-xs uppercase tracking-[0.2em] mb-2 text-white/80">Descripción Corta</label>
            <input type="text" value={formData.short_description} onChange={(e) => setFormData({...formData, short_description: e.target.value})} required className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none" />
          </div>
          
          <div className="mb-4">
            <label className="block text-xs uppercase tracking-[0.2em] mb-2 text-white/80">Descripción Completa</label>
            <textarea value={formData.full_description} onChange={(e) => setFormData({...formData, full_description: e.target.value})} rows="4" className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none" />
          </div>
          
          <div className="mb-4">
            <label className="block text-xs uppercase tracking-[0.2em] mb-3 text-white/80">Imágenes</label>
            <ImageUploader
              images={formData.images}
              mainIndex={formData.main_image_index || 0}
              onChange={(imgs) => setFormData((prev) => ({ ...prev, images: imgs }))}
              onMainChange={(idx) => setFormData((prev) => ({ ...prev, main_image_index: idx }))}
            />
          </div>
          
          {/* Opciones de Destacado */}
          <div className="mb-6 p-4 border border-white/10 bg-[#121212]">
            <h4 className="text-xs uppercase tracking-[0.2em] mb-4 text-white/80 font-semibold">Opciones de Destacado</h4>
            <label className="flex items-center gap-3 cursor-pointer group">
              <input 
                type="checkbox" 
                checked={formData.featured_home || false}
                onChange={(e) => setFormData({...formData, featured_home: e.target.checked})}
                className="w-5 h-5 bg-[#0A0A0A] border border-white/20 rounded appearance-none checked:bg-white cursor-pointer"
              />
              <span className="text-sm text-white/80 group-hover:text-white">
                Destacar en Inicio
              </span>
            </label>
            <p className="text-xs text-white/40 mt-2 ml-8">
              Este producto representará la categoría "{formData.category || 'seleccionada'}" en la página principal.
              Solo puede haber un producto destacado por categoría.
            </p>
          </div>
          
          <div className="flex gap-4 mt-8">
            <button type="submit" data-testid="save-product-button" className="flex-1 bg-white text-black hover:bg-gray-200 transition-colors duration-300 px-6 py-3 rounded-sm text-xs uppercase tracking-wider font-semibold">
              Guardar
            </button>
            <button type="button" onClick={onClose} className="flex-1 border border-white/20 hover:bg-white/5 transition-colors duration-300 px-6 py-3 rounded-sm text-xs uppercase tracking-wider font-semibold">
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ConfigPanel = ({ config, onUpdate }) => {
  const [formData, setFormData] = useState({
    ...config,
    show_prices: config.show_prices !== false,
    show_virtual_tryon: config.show_virtual_tryon === true,
    whatsapp_enabled: config.whatsapp_enabled !== false,
    low_stock_threshold: typeof config.low_stock_threshold === 'number' ? config.low_stock_threshold : 30,
    hero_backgrounds: config.hero_backgrounds || [],
    whatsapp_number: config.whatsapp_number || ''
  });
  const [saving, setSaving] = useState(false);
  const [newBackgroundUrl, setNewBackgroundUrl] = useState('');

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/config', formData);
      toast.success('Configuración actualizada');
      onUpdate();
    } catch (error) {
      toast.error('Error actualizando configuración');
    } finally {
      setSaving(false);
    }
  };

  const addBackground = () => {
    if (newBackgroundUrl && !formData.hero_backgrounds.includes(newBackgroundUrl)) {
      setFormData({
        ...formData,
        hero_backgrounds: [...formData.hero_backgrounds, newBackgroundUrl]
      });
      setNewBackgroundUrl('');
    }
  };

  const removeBackground = (index) => {
    setFormData({
      ...formData,
      hero_backgrounds: formData.hero_backgrounds.filter((_, i) => i !== index)
    });
  };

  const moveBackground = (index, direction) => {
    const newBackgrounds = [...formData.hero_backgrounds];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newBackgrounds.length) return;
    [newBackgrounds[index], newBackgrounds[newIndex]] = [newBackgrounds[newIndex], newBackgrounds[index]];
    setFormData({ ...formData, hero_backgrounds: newBackgrounds });
  };

  return (
    <div>
      <h2 className="text-xl md:text-2xl font-semibold mb-6 md:mb-8">Configuración del Sitio</h2>
      
      {/* Control de Visibilidad de Precios - DESTACADO */}
      <div className="bg-gradient-to-r from-[#1a1a1a] to-[#0A0A0A] border-2 border-white/20 p-4 md:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-xs md:text-sm uppercase tracking-[0.2em] mb-2 font-semibold flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Visibilidad de Precios
            </h3>
            <p className="text-[10px] md:text-xs text-white/50">
              {formData.show_prices 
                ? 'Los precios son visibles para visitantes'
                : 'Los precios están ocultos (solo admin)'
              }
            </p>
          </div>
          <button
            onClick={() => setFormData({...formData, show_prices: !formData.show_prices})}
            data-testid="toggle-show-prices"
            className={`relative w-16 h-8 rounded-full transition-all duration-300 flex-shrink-0 ${
              formData.show_prices ? 'bg-green-500' : 'bg-gray-600'
            }`}
          >
            <span className={`absolute w-6 h-6 bg-white rounded-full top-1 transition-all duration-300 shadow-lg ${
              formData.show_prices ? 'left-9' : 'left-1'
            }`} />
          </button>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <span className={`px-3 py-1 text-[10px] md:text-xs uppercase tracking-wider font-semibold ${
            formData.show_prices 
              ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
              : 'bg-red-500/20 text-red-400 border border-red-500/30'
          }`}>
            {formData.show_prices ? 'PRECIOS VISIBLES' : 'PRECIOS OCULTOS'}
          </span>
        </div>
      </div>

      {/* Control de Probador Virtual */}
      <div className="bg-gradient-to-r from-[#1a1a1a] to-[#0A0A0A] border-2 border-purple-500/20 p-4 md:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-xs md:text-sm uppercase tracking-[0.2em] mb-2 font-semibold flex items-center gap-2">
              <span className="text-purple-400">🪞</span>
              Probador Virtual
            </h3>
            <p className="text-[10px] md:text-xs text-white/50">
              {formData.show_virtual_tryon 
                ? 'El probador virtual está disponible para clientes'
                : 'El probador virtual está desactivado'
              }
            </p>
          </div>
          <button
            onClick={() => setFormData({...formData, show_virtual_tryon: !formData.show_virtual_tryon})}
            data-testid="toggle-virtual-tryon"
            className={`relative w-16 h-8 rounded-full transition-all duration-300 flex-shrink-0 ${
              formData.show_virtual_tryon ? 'bg-purple-500' : 'bg-gray-600'
            }`}
          >
            <span className={`absolute w-6 h-6 bg-white rounded-full top-1 transition-all duration-300 shadow-lg ${
              formData.show_virtual_tryon ? 'left-9' : 'left-1'
            }`} />
          </button>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <span className={`px-3 py-1 text-[10px] md:text-xs uppercase tracking-wider font-semibold ${
            formData.show_virtual_tryon 
              ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' 
              : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
          }`}>
            {formData.show_virtual_tryon ? 'PROBADOR ACTIVO' : 'PROBADOR INACTIVO'}
          </span>
        </div>
      </div>

      {/* Control de Stock Bajo (Última Curva) */}
      <div className="bg-gradient-to-r from-[#1a1a1a] to-[#0A0A0A] border-2 border-red-500/20 p-4 md:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-xs md:text-sm uppercase tracking-[0.2em] mb-2 font-semibold flex items-center gap-2">
              <span className="text-red-400">⚠</span>
              Umbral de Stock Bajo
            </h3>
            <p className="text-[10px] md:text-xs text-white/50 leading-relaxed">
              Cuando el stock de un producto sea <strong className="text-red-400">menor o igual</strong> a este número, se mostrará el marco rojo y el aviso <em>"Última curva"</em> en el catálogo público y en el formulario de pre-pedido. Se desactiva poniendo <strong>0</strong>.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={() => setFormData({ ...formData, low_stock_threshold: Math.max(0, (formData.low_stock_threshold || 0) - 5) })}
              data-testid="low-stock-minus"
              className="w-9 h-9 flex items-center justify-center bg-[#121212] border border-white/10 hover:border-red-400/50 text-lg"
            >−</button>
            <input
              type="number"
              min="0"
              max="9999"
              value={formData.low_stock_threshold ?? 30}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                setFormData({ ...formData, low_stock_threshold: isNaN(v) ? 0 : Math.max(0, Math.min(9999, v)) });
              }}
              data-testid="low-stock-threshold-input"
              className="w-20 h-9 bg-[#121212] border border-red-500/30 focus:border-red-400 text-center text-base font-mono outline-none"
            />
            <button
              type="button"
              onClick={() => setFormData({ ...formData, low_stock_threshold: Math.min(9999, (formData.low_stock_threshold || 0) + 5) })}
              data-testid="low-stock-plus"
              className="w-9 h-9 flex items-center justify-center bg-[#121212] border border-white/10 hover:border-red-400/50 text-lg"
            >+</button>
            <span className="text-[10px] uppercase tracking-wider text-white/40 ml-1">piezas</span>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <span className={`px-3 py-1 text-[10px] md:text-xs uppercase tracking-wider font-semibold ${
            (formData.low_stock_threshold || 0) > 0
              ? 'bg-red-500/20 text-red-400 border border-red-500/30'
              : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
          }`}>
            {(formData.low_stock_threshold || 0) > 0
              ? `ALERTA ACTIVA: STOCK ≤ ${formData.low_stock_threshold}`
              : 'ALERTA DESACTIVADA'}
          </span>
        </div>
      </div>
      
      {/* Hero Section Config */}
      <div className="bg-[#0A0A0A] border border-white/10 p-4 md:p-6 mb-6">
        <h3 className="text-xs md:text-sm uppercase tracking-[0.2em] mb-4 font-semibold">Hero Section</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-[10px] md:text-xs uppercase tracking-[0.2em] mb-2 text-white/80">Título</label>
            <input type="text" value={formData.hero_title || ''} onChange={(e) => setFormData({...formData, hero_title: e.target.value})} className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none" />
          </div>
          <div>
            <label className="block text-[10px] md:text-xs uppercase tracking-[0.2em] mb-2 text-white/80">Subtítulo</label>
            <input type="text" value={formData.hero_subtitle || ''} onChange={(e) => setFormData({...formData, hero_subtitle: e.target.value})} className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none" />
          </div>
        </div>
        
        {/* Imágenes de Fondo del Hero */}
        <div className="border-t border-white/10 pt-4">
          <label className="block text-[10px] md:text-xs uppercase tracking-[0.2em] mb-3 text-white/80">
            Imágenes de Fondo del Hero
          </label>
          <p className="text-[10px] text-white/40 mb-4">
            Agrega URLs de imágenes para el carrusel de fondo. La primera imagen será la principal.
          </p>
          
          {/* Input para agregar nueva imagen */}
          <div className="flex gap-2 mb-4">
            <input
              type="url"
              value={newBackgroundUrl}
              onChange={(e) => setNewBackgroundUrl(e.target.value)}
              placeholder="https://ejemplo.com/imagen.jpg"
              className="flex-1 bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-2 text-white text-sm outline-none"
            />
            <button
              onClick={addBackground}
              type="button"
              className="px-4 py-2 bg-white text-black text-xs uppercase tracking-wider hover:bg-gray-200 transition-colors"
            >
              Agregar
            </button>
          </div>
          
          {/* Lista de imágenes */}
          {formData.hero_backgrounds.length > 0 ? (
            <div className="space-y-2">
              {formData.hero_backgrounds.map((url, index) => (
                <div key={index} className="flex items-center gap-2 bg-[#121212] border border-white/10 p-2">
                  <img src={url} alt={`Fondo ${index + 1}`} className="w-16 h-10 object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-white/60 truncate">{url}</p>
                    {index === 0 && (
                      <span className="text-[9px] bg-green-500/20 text-green-400 px-1.5 py-0.5 uppercase">Principal</span>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={() => moveBackground(index, -1)}
                      disabled={index === 0}
                      className="p-1.5 hover:bg-white/10 disabled:opacity-30 text-white/60"
                      title="Mover arriba"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => moveBackground(index, 1)}
                      disabled={index === formData.hero_backgrounds.length - 1}
                      className="p-1.5 hover:bg-white/10 disabled:opacity-30 text-white/60"
                      title="Mover abajo"
                    >
                      ↓
                    </button>
                    <button
                      onClick={() => removeBackground(index)}
                      className="p-1.5 hover:bg-red-500/20 text-red-400"
                      title="Eliminar"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-white/40 text-center py-4 border border-dashed border-white/10">
              No hay imágenes de fondo configuradas. Se usará la imagen por defecto.
            </p>
          )}
        </div>
      </div>
      
      {/* Contacto */}
      <div className="bg-[#0A0A0A] border border-white/10 p-4 md:p-6 mb-6">
        <h3 className="text-xs md:text-sm uppercase tracking-[0.2em] mb-4 font-semibold">Contacto</h3>
        
        {/* Toggle visibilidad WhatsApp */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 pb-5 border-b border-white/5">
          <div>
            <p className="text-xs uppercase tracking-[0.15em] font-semibold mb-1">Botón WhatsApp visible</p>
            <p className="text-[10px] text-white/50">
              {formData.whatsapp_enabled
                ? 'El botón flotante y la opción en /contacto son visibles para visitantes.'
                : 'WhatsApp está OCULTO en toda la web pública.'}
            </p>
          </div>
          <button
            onClick={() => setFormData({ ...formData, whatsapp_enabled: !formData.whatsapp_enabled })}
            data-testid="toggle-whatsapp"
            className={`relative w-16 h-8 rounded-full transition-all duration-300 flex-shrink-0 ${
              formData.whatsapp_enabled ? 'bg-green-500' : 'bg-gray-600'
            }`}
          >
            <span
              className={`absolute w-6 h-6 bg-white rounded-full top-1 transition-all duration-300 shadow-lg ${
                formData.whatsapp_enabled ? 'left-9' : 'left-1'
              }`}
            />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] md:text-xs uppercase tracking-[0.2em] mb-2 text-white/80">
              Número de WhatsApp
            </label>
            <input 
              type="text" 
              value={formData.whatsapp_number || ''} 
              onChange={(e) => setFormData({...formData, whatsapp_number: e.target.value})} 
              placeholder="573001234567 (sin + ni espacios)" 
              className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none" 
            />
            <p className="text-[10px] text-white/40 mt-1">
              Este número se usará para el botón de WhatsApp y para compartir facturas
            </p>
          </div>
          
          <div>
            <label className="block text-[10px] md:text-xs uppercase tracking-[0.2em] mb-2 text-white/80">Email</label>
            <input 
              type="email" 
              value={formData.contact_email || ''} 
              onChange={(e) => setFormData({...formData, contact_email: e.target.value})} 
              placeholder="contact@example.com" 
              className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none" 
            />
          </div>
        </div>
      </div>
      
      <button onClick={handleSave} disabled={saving} data-testid="save-config-button" className="w-full sm:w-auto bg-white text-black hover:bg-gray-200 transition-colors duration-300 px-8 py-4 rounded-sm text-xs uppercase tracking-wider font-semibold">
        {saving ? 'Guardando...' : 'Guardar Configuración'}
      </button>
    </div>
  );
};

// ===== CATEGORIES PANEL =====
const CategoriesPanel = ({ categories, onUpdate }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', slug: '', description: '', image: '' });
  const [saving, setSaving] = useState(false);

  const openCreate = () => {
    setEditing(null);
    setFormData({ name: '', slug: '', description: '', image: '' });
    setShowDialog(true);
  };

  const openEdit = (category) => {
    setEditing(category);
    setFormData({
      name: category.name || '',
      slug: category.slug || '',
      description: category.description || '',
      image: category.image || ''
    });
    setShowDialog(true);
  };

  const generateSlug = (name) => {
    return name.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData({
      ...formData,
      name,
      slug: editing ? formData.slug : generateSlug(name)
    });
  };

  const handleSave = async () => {
    if (!formData.name || !formData.slug) {
      toast.error('Nombre y slug son requeridos');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/categories/${editing.id}`, formData);
        toast.success('Categoría actualizada');
      } else {
        await api.post('/categories', formData);
        toast.success('Categoría creada');
      }
      setShowDialog(false);
      onUpdate();
    } catch (error) {
      toast.error('Error guardando categoría');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Seguro que deseas eliminar esta categoría?')) return;
    try {
      await api.delete(`/categories/${id}`);
      toast.success('Categoría eliminada');
      onUpdate();
    } catch (error) {
      toast.error('Error eliminando categoría');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Categorías</h2>
          <p className="text-sm text-white/60">{categories.length} categorías registradas</p>
        </div>
        <button onClick={openCreate} data-testid="add-category-button" className="bg-white text-black hover:bg-gray-200 transition-colors duration-300 px-6 py-3 rounded-sm text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Nueva Categoría
        </button>
      </div>
      
      <div className="grid grid-cols-1 gap-4">
        {categories.map((category) => (
          <div key={category.id} data-testid={`category-row-${category.id}`} className="bg-[#0A0A0A] border border-white/10 p-4 flex items-center justify-between hover:border-white/20 transition-all duration-300">
            <div className="flex items-center gap-4 flex-1">
              {category.image && (
                <img src={category.image} alt={category.name} className="w-12 h-12 object-cover rounded" />
              )}
              <div className="flex-1">
                <h3 className="font-semibold text-sm mb-1">{category.name}</h3>
                <p className="text-xs text-white/60">Slug: {category.slug}</p>
                {category.description && (
                  <p className="text-xs text-white/40 mt-1">{category.description}</p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => openEdit(category)} data-testid={`edit-category-${category.id}`} className="p-2 border border-white/10 hover:border-white/40 rounded-sm transition-all duration-300">
                <Edit className="w-4 h-4" />
              </button>
              <button onClick={() => handleDelete(category.id)} data-testid={`delete-category-${category.id}`} className="p-2 border border-white/10 hover:border-red-500/40 hover:text-red-500 rounded-sm transition-all duration-300">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Category Dialog */}
      {showDialog && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowDialog(false)}>
          <div className="bg-[#0A0A0A] border border-white/10 p-8 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-semibold mb-6">{editing ? 'Editar Categoría' : 'Nueva Categoría'}</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] mb-2 text-white/80">Nombre</label>
                <input 
                  type="text" 
                  value={formData.name} 
                  onChange={handleNameChange}
                  placeholder="Ej: Camisetas"
                  className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none" 
                />
              </div>
              
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] mb-2 text-white/80">Slug (URL)</label>
                <input 
                  type="text" 
                  value={formData.slug} 
                  onChange={(e) => setFormData({...formData, slug: e.target.value})}
                  placeholder="camisetas"
                  className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none" 
                />
              </div>
              
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] mb-2 text-white/80">Descripción (opcional)</label>
                <textarea 
                  value={formData.description} 
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows="2"
                  className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none" 
                />
              </div>
              
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] mb-2 text-white/80">Imagen URL (opcional)</label>
                <input 
                  type="url" 
                  value={formData.image} 
                  onChange={(e) => setFormData({...formData, image: e.target.value})}
                  placeholder="https://..."
                  className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none" 
                />
              </div>
            </div>
            
            <div className="flex gap-4 mt-8">
              <button onClick={handleSave} disabled={saving} className="flex-1 bg-white text-black hover:bg-gray-200 transition-colors duration-300 px-6 py-3 rounded-sm text-xs uppercase tracking-wider font-semibold">
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
              <button onClick={() => setShowDialog(false)} className="flex-1 border border-white/20 hover:bg-white/5 transition-colors duration-300 px-6 py-3 rounded-sm text-xs uppercase tracking-wider font-semibold">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ===== BRANDS PANEL =====
const BrandsPanel = ({ brands, onUpdate }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', slug: '', description: '', logo: '' });
  const [saving, setSaving] = useState(false);

  const openCreate = () => {
    setEditing(null);
    setFormData({ name: '', slug: '', description: '', logo: '' });
    setShowDialog(true);
  };

  const openEdit = (brand) => {
    setEditing(brand);
    setFormData({
      name: brand.name || '',
      slug: brand.slug || '',
      description: brand.description || '',
      logo: brand.logo || ''
    });
    setShowDialog(true);
  };

  const generateSlug = (name) => {
    return name.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData({
      ...formData,
      name,
      slug: editing ? formData.slug : generateSlug(name)
    });
  };

  const handleSave = async () => {
    if (!formData.name || !formData.slug) {
      toast.error('Nombre y slug son requeridos');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/brands/${editing.id}`, formData);
        toast.success('Marca actualizada');
      } else {
        await api.post('/brands', formData);
        toast.success('Marca creada');
      }
      setShowDialog(false);
      onUpdate();
    } catch (error) {
      toast.error('Error guardando marca');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Seguro que deseas eliminar esta marca?')) return;
    try {
      await api.delete(`/brands/${id}`);
      toast.success('Marca eliminada');
      onUpdate();
    } catch (error) {
      toast.error('Error eliminando marca');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Marcas</h2>
          <p className="text-sm text-white/60">{brands.length} marcas registradas</p>
        </div>
        <button onClick={openCreate} data-testid="add-brand-button" className="bg-white text-black hover:bg-gray-200 transition-colors duration-300 px-6 py-3 rounded-sm text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Nueva Marca
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {brands.map((brand) => (
          <div key={brand.id} data-testid={`brand-row-${brand.id}`} className="bg-[#0A0A0A] border border-white/10 p-4 hover:border-white/20 transition-all duration-300">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                {brand.logo && (
                  <img src={brand.logo} alt={brand.name} className="w-10 h-10 object-contain rounded" />
                )}
                <div>
                  <h3 className="font-semibold text-sm">{brand.name}</h3>
                  <p className="text-xs text-white/40">{brand.slug}</p>
                </div>
              </div>
            </div>
            {brand.description && (
              <p className="text-xs text-white/60 mb-3">{brand.description}</p>
            )}
            <div className="flex gap-2">
              <button onClick={() => openEdit(brand)} data-testid={`edit-brand-${brand.id}`} className="flex-1 p-2 border border-white/10 hover:border-white/40 rounded-sm transition-all duration-300 text-xs">
                Editar
              </button>
              <button onClick={() => handleDelete(brand.id)} data-testid={`delete-brand-${brand.id}`} className="p-2 border border-white/10 hover:border-red-500/40 hover:text-red-500 rounded-sm transition-all duration-300">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Brand Dialog */}
      {showDialog && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowDialog(false)}>
          <div className="bg-[#0A0A0A] border border-white/10 p-8 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-semibold mb-6">{editing ? 'Editar Marca' : 'Nueva Marca'}</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] mb-2 text-white/80">Nombre</label>
                <input 
                  type="text" 
                  value={formData.name} 
                  onChange={handleNameChange}
                  placeholder="Ej: Hugo Boss"
                  className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none" 
                />
              </div>
              
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] mb-2 text-white/80">Slug (URL)</label>
                <input 
                  type="text" 
                  value={formData.slug} 
                  onChange={(e) => setFormData({...formData, slug: e.target.value})}
                  placeholder="hugo-boss"
                  className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none" 
                />
              </div>
              
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] mb-2 text-white/80">Descripción (opcional)</label>
                <textarea 
                  value={formData.description} 
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows="2"
                  className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none" 
                />
              </div>
              
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] mb-2 text-white/80">Logo URL (opcional)</label>
                <input 
                  type="url" 
                  value={formData.logo} 
                  onChange={(e) => setFormData({...formData, logo: e.target.value})}
                  placeholder="https://..."
                  className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none" 
                />
              </div>
            </div>
            
            <div className="flex gap-4 mt-8">
              <button onClick={handleSave} disabled={saving} className="flex-1 bg-white text-black hover:bg-gray-200 transition-colors duration-300 px-6 py-3 rounded-sm text-xs uppercase tracking-wider font-semibold">
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
              <button onClick={() => setShowDialog(false)} className="flex-1 border border-white/20 hover:bg-white/5 transition-colors duration-300 px-6 py-3 rounded-sm text-xs uppercase tracking-wider font-semibold">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
